import { createFileRoute } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { FiBook, FiDownload, FiImage } from 'react-icons/fi';
import { apiJson, extractApiErrorMessage } from '@/lib/api';
import { downloadProfessionalSectionExport } from '@/lib/pro-client-export';
import type { JournalEntryListItem, ProfessionalClientListResponse } from '@/lib/types';

type JournalSection = { title: string; data: JournalEntryListItem[] };

function parseEntryDate(entryDate: string): { dow: string; dayNum: string } {
  const d = new Date(entryDate + (entryDate.includes('T') ? '' : 'T12:00:00'));
  const dow = d.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();
  const dayNum = String(d.getDate());
  return { dow, dayNum };
}

function buildSections(entries: JournalEntryListItem[]): JournalSection[] {
  if (entries.length === 0) return [];
  const sorted = [...entries].sort(
    (a, b) => new Date(b.entry_date).getTime() - new Date(a.entry_date).getTime()
  );
  const map = new Map<string, JournalEntryListItem[]>();
  const order: string[] = [];
  for (const e of sorted) {
    const d = new Date(e.entry_date + (e.entry_date.includes('T') ? '' : 'T12:00:00'));
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    if (!map.has(key)) {
      map.set(key, []);
      order.push(key);
    }
    map.get(key)!.push(e);
  }
  return order
    .map((key) => {
      const data = map.get(key);
      const first = data?.[0];
      if (!data?.length || !first) return null;
      const d = new Date(first.entry_date + (first.entry_date.includes('T') ? '' : 'T12:00:00'));
      const title = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      return { title, data };
    })
    .filter((s): s is JournalSection => s !== null);
}

function previewTitle(item: JournalEntryListItem): string {
  if (item.title?.trim()) return item.title.trim();
  if (item.entry_type === 'PHONE_CALL') return 'Phone call';
  return 'Journal entry';
}

function previewBody(item: JournalEntryListItem): string {
  const b = item.body?.trim() ?? '';
  if (b.length === 0) return '';
  if (b.length <= 200) return b;
  return b.slice(0, 197) + '…';
}

function attachmentCount(item: JournalEntryListItem): number {
  return item.attachment_count ?? item.attachments?.length ?? 0;
}

function ProClientJournalPage() {
  const { inviteId } = Route.useParams();
  const id = Number(inviteId);
  const { getToken } = useAuth();
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  const clientsQuery = useQuery({
    queryKey: ['professional-clients'],
    queryFn: () => apiJson<ProfessionalClientListResponse>(getToken, '/v1/professional/clients'),
  });
  const invite = useMemo(
    () => clientsQuery.data?.clients.find((c) => c.id === id),
    [clientsQuery.data, id]
  );
  const canView = !!invite?.share_journal;

  const entriesQuery = useQuery({
    queryKey: ['professional-client-journal', id],
    queryFn: () =>
      apiJson<JournalEntryListItem[]>(
        getToken,
        `/v1/professional/clients/${id}/journal?limit=100&offset=0`
      ),
    enabled: canView,
  });

  const sections = useMemo(
    () => buildSections(entriesQuery.data ?? []),
    [entriesQuery.data]
  );

  const handleExport = async () => {
    setExportError(null);
    try {
      await downloadProfessionalSectionExport(getToken, id, 'journal');
    } catch (e: unknown) {
      setExportError(extractApiErrorMessage(e, 'Could not download export.'));
    }
  };

  const bodyStyle = {
    fontFamily:
      "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  } as const;

  return (
    <div style={bodyStyle}>
      <div className='flex items-center justify-between gap-4 mb-4'>
        <h2 className='text-lg font-semibold text-gray-900'>Journal</h2>
        <button
          type='button'
          onClick={handleExport}
          className='flex items-center gap-2 px-3 py-2 rounded-xl border border-[#018A9D] text-sm font-semibold text-[#018A9D] hover:bg-[#018A9D]/5 shrink-0'
        >
          <FiDownload className='w-4 h-4' />
          Download PDF
        </button>
      </div>
      {exportError ? <p className='text-sm text-red-600 mb-2'>{exportError}</p> : null}

      <p className='text-sm text-gray-500 mb-4'>
        Entries from the co-parent who shared this case (read-only).
      </p>

      {entriesQuery.isLoading ? (
        <p className='text-center text-gray-500 py-12'>Loading…</p>
      ) : null}
      {entriesQuery.isError ? (
        <p className='text-center text-red-600 py-8'>Couldn&apos;t load journal.</p>
      ) : null}

      {!entriesQuery.isLoading && !entriesQuery.isError && sections.length === 0 ? (
        <div className='text-center py-16'>
          <FiBook className='w-12 h-12 mx-auto text-gray-300' />
          <p className='mt-4 text-lg font-bold text-gray-800'>No journal entries</p>
        </div>
      ) : null}

      {sections.map((section) => (
        <section key={section.title} className='mb-6'>
          <h3 className='text-sm font-semibold text-gray-700 border-b border-gray-200 pb-2 mb-2'>
            {section.title}
          </h3>
          <ul className='space-y-2'>
            {section.data.map((item) => {
              const { dow, dayNum } = parseEntryDate(item.entry_date);
              const ac = attachmentCount(item);
              const open = expandedId === item.id;
              return (
                <li key={item.id}>
                  <button
                    type='button'
                    onClick={() => setExpandedId(open ? null : item.id)}
                    className='w-full text-left flex gap-3 bg-white rounded-xl border border-gray-100 p-3 hover:border-[#018A9D]/30 transition-colors'
                  >
                    <div className='w-11 shrink-0 text-center pt-0.5'>
                      <div className='text-[10px] font-bold text-gray-400 tracking-wide'>{dow}</div>
                      <div className='text-[26px] font-bold text-gray-900 leading-tight'>{dayNum}</div>
                    </div>
                    <div className='flex-1 min-w-0'>
                      <p className='font-bold text-gray-900'>{previewTitle(item)}</p>
                      <p className='text-sm text-gray-500 mt-1 line-clamp-3'>
                        {open ? item.body?.trim() || '-' : previewBody(item)}
                      </p>
                      {item.entry_type === 'PHONE_CALL' ? (
                        <p className='text-xs text-gray-400 mt-2'>Phone call entry</p>
                      ) : null}
                    </div>
                    {ac > 0 ? (
                      <div className='relative w-12 h-12 rounded-lg bg-[#018A9D]/10 flex items-center justify-center shrink-0'>
                        <FiImage className='w-5 h-5 text-[#018A9D] opacity-90' />
                      </div>
                    ) : (
                      <div className='w-12 shrink-0' />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}

export const Route = createFileRoute('/pro/clients/$inviteId/journal')({
  component: ProClientJournalPage,
});
